import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	Button,
	// GoogleButton,
	Input,
	InputWrapperAuth,
} from '@/components/ui';
import { z } from 'zod';
import { ArrowRightCircle, Lock, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { saveStepData } from '@/utils/localStorageUtils';
import { useEffect } from 'react';
import { useLoginUser, usePatchUserComplement } from '@/services';
import { useResumeSchedulingPayAfterAuth } from '@/hooks/useResumeSchedulingPayAfterAuth';
import {
	clearSchedulingPayDraftSilently,
	decodeUrl,
	encodeUrl,
	isSchedulingPayAfterAuthPending,
	readSchedulingPayDraft,
	saveSchedulingPayDraft,
	schedulingSelectedDateStillBookable,
	SCHEDULING_REGISTRATION_PENDING_KEY,
} from '@/utils';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';
import { GoogleRegister } from './registerGoogle/GoogleRegister';

const registerSchema = z
	.object({
		email: z.string().max(255, 'Email inválido').email('Email inválido'),
		password: z
			.string()
			.min(6, 'A senha deve ter no mínimo 6 caracteres')
			.max(255, 'A senha deve ter no máximo 255 caracteres')
			.regex(
				/^[0-9A-Za-zÀ-ÖØ-öø-ÿ!@#$%^&*()_+\-= [\]{};':"\\|,.<>/?~`]+$/,
				'A senha contém caracteres inválidos',
			)
			.regex(
				/^(?=.*[A-Za-z])(?=.*\d).+$/,
				'A senha deve conter pelo menos uma letra e um número',
			),
		repeat_password: z.string(),
	})
	.refine((data) => data.password === data.repeat_password, {
		message: 'As senhas não coincidem',
		path: ['repeat_password'],
	});

const loginSchema = z.object({
	email: z.string().max(255, 'Email inválido').email('Email inválido'),
	password: z.string().min(1, 'A senha é obrigatória'),
});

export const STORAGE_USER_KEYS = {
	USER_AUTH: 'u_auth',
	STEP_INIT: 's_i',
	STEP_COMPLEMENTATION: 's_c',
};

const PublicSessionAuth = () => {
	/** Fluxo pós-Pagar no agendamento: prioriza cadastro de conta nova. */
	const [isLogin, setIsLogin] = useState(
		() => !isSchedulingPayAfterAuthPending(),
	);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	type PublicSessionData = Sessions.Create & { user_id?: string };

	const [sessionData, setSessionData] = useState<PublicSessionData>();
	const [isSchedulingPublicRegister, setIsSchedulingPublicRegister] =
		useState(false);

	type LoginFormData = z.infer<typeof loginSchema>;
	type RegisterFormData = z.infer<typeof registerSchema>;

	const {
		register: registerLogin,
		handleSubmit: handleSubmitLogin,
		watch: watchLogin,
		formState: { errors: errorsLogin },
	} = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
	});

	const {
		register: registerRegister,
		handleSubmit: handleSubmitRegister,
		watch: watchRegister,
		formState: { errors: errorsRegister },
	} = useForm<RegisterFormData>({
		resolver: zodResolver(registerSchema),
	});

	const isPublicRegisterScheduling =
		localStorage.getItem('is_public_register');

	useEffect(() => {
		const stored = readSchedulingPayDraft();
		if (stored) {
			setSessionData(stored);
		}
		if (isPublicRegisterScheduling) {
			const storageData = localStorage.getItem('session_data');
			if (storageData) {
				setSessionData(JSON.parse(storageData));
				setIsSchedulingPublicRegister(true);
			}
		}
	}, [isPublicRegisterScheduling]);

	const { resumeSchedulingPayAfterAuth } = useResumeSchedulingPayAfterAuth();

	const handleLoginSuccess = (_data: Users.CreateResponse) => {
		void resumeSchedulingPayAfterAuth(navigate);
	};

	const { mutateAsync } = useLoginUser(handleLoginSuccess);

	const onLoginSubmit = async (data: LoginFormData) =>
		await mutateAsync(data);

	const onRegisterSubmit = (data: RegisterFormData) => {
		saveStepData(
			STORAGE_USER_KEYS.USER_AUTH,
			STORAGE_USER_KEYS.STEP_INIT,
			data,
		);

		localStorage.setItem('isSchedulingFlow', 'true');
		localStorage.setItem(SCHEDULING_REGISTRATION_PENDING_KEY, 'true');

		navigate('/cadastro/info-complemento');
	};

	const encodedParams = searchParams.get('d');
	const decodedParams = encodedParams
		? decodeUrl<{ s: string; data?: string }>(encodedParams)
		: null;

	useEffect(() => {
		if (!decodedParams?.data) return;

		const parsed = JSON.parse(decodedParams.data) as Sessions.Create;
		setSessionData(parsed);
		saveSchedulingPayDraft(parsed);

		const encodeParams = encodeUrl<{ s: string }>({ s: '3' });
		searchParams.set('d', encodeParams);
		navigate(`?${searchParams.toString()}`, { replace: true });
	}, [encodedParams, decodedParams?.data, navigate, searchParams]);

	const iconCommonClass = 'w-4 h-4 text-zinc-500';

	const { mutate: createUserMutate } = usePatchUserComplement(() => {
		const schedulingBody = readSchedulingPayDraft();
		if (!schedulingBody?.selected_date) {
			clearSchedulingPayDraftSilently();
			return;
		}

		if (
			!schedulingSelectedDateStillBookable(schedulingBody.selected_date)
		) {
			clearSchedulingPayDraftSilently();
			return;
		}

		localStorage.removeItem('is_public_register');
		localStorage.removeItem('rAuth');
		void resumeSchedulingPayAfterAuth(navigate);
	});

	const handleOnUserCreate = (data: Users.CreateComplement) => {
		createUserMutate(data);
	};

	if (isSchedulingPublicRegister && sessionData?.user_id) {
		return (
			<GoogleRegister
				handleOnRegisterComplement={handleOnUserCreate}
				userId={sessionData.user_id}
			/>
		);
	}

	return (
		<section
			className="flex w-full justify-center"
			data-testid={E2E_SCHEDULING.PUBLIC_AUTH_ROOT}
		>
			<form
				onSubmit={
					isLogin
						? handleSubmitLogin(onLoginSubmit)
						: handleSubmitRegister(onRegisterSubmit)
				}
				className="flex w-full max-w-[440px] flex-col gap-4 rounded-[2rem] bg-white p-5 shadow-sm md:p-8"
			>
				<div className="space-y-1 text-center">
					<h1 className="text-xl font-black tracking-tight text-zinc-900 md:text-2xl">
						Acesse sua conta
					</h1>
					<p className="text-[10px] font-medium text-zinc-400 md:text-xs">
						Para continuar o agendamento, faça login ou crie uma
						conta.
					</p>
				</div>

				<div className="w-full">
					<div className="flex w-full justify-center gap-1 rounded-lg bg-zinc-100 p-1">
						<button
							type="button"
							data-testid={E2E_SCHEDULING.AUTH_TAB_REGISTER}
							className={`w-full cursor-pointer rounded-md border-none px-0 py-1.5 text-center text-xs font-bold focus:outline-none ${
								!isLogin
									? 'bg-white text-zinc-900 shadow-sm'
									: 'bg-transparent text-zinc-500'
							}`}
							onClick={() => setIsLogin(false)}
						>
							Cadastro
						</button>
						<button
							type="button"
							data-testid={E2E_SCHEDULING.AUTH_TAB_LOGIN}
							className={`w-full cursor-pointer rounded-md border-none px-0 py-1.5 text-center text-xs font-bold focus:outline-none ${
								isLogin
									? 'bg-white text-zinc-900 shadow-sm'
									: 'bg-transparent text-zinc-500'
							}`}
							onClick={() => setIsLogin(true)}
						>
							Entrar
						</button>
					</div>
				</div>

				<div className="space-y-3">
					{isLogin ? (
						<>
							<div className="space-y-1">
								<label className="ml-1 text-[8px] font-black uppercase tracking-widest text-zinc-500">
									E-mail
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Mail className={iconCommonClass} />}
									error={errorsLogin.email?.message}
									value={watchLogin('email')}
									displayError
								>
									<Input
										{...registerLogin('email')}
										data-testid={
											E2E_SCHEDULING.AUTH_LOGIN_EMAIL
										}
										placeholder="nome@exemplo.com"
										className="h-9 border-zinc-200 text-xs focus:border-zinc-400"
									/>
								</InputWrapperAuth>
							</div>

							<div className="space-y-1">
								<div className="flex items-center justify-between px-1">
									<label className="text-[8px] font-black uppercase tracking-widest text-zinc-500">
										Senha
									</label>
								</div>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errorsLogin.password?.message}
									value={watchLogin('password')}
									displayError
								>
									<Input
										type="password"
										{...registerLogin('password')}
										data-testid={
											E2E_SCHEDULING.AUTH_LOGIN_PASSWORD
										}
										placeholder="********"
										className="h-9 border-zinc-200 text-xs focus:border-zinc-400"
									/>
								</InputWrapperAuth>
							</div>
						</>
					) : (
						<>
							<div className="space-y-1">
								<label className="ml-1 text-[8px] font-black uppercase tracking-widest text-zinc-500">
									E-mail
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Mail className={iconCommonClass} />}
									error={errorsRegister.email?.message}
									value={watchRegister('email')}
									displayError
								>
									<Input
										{...registerRegister('email')}
										data-testid={
											E2E_SCHEDULING.AUTH_REGISTER_EMAIL
										}
										placeholder="nome@exemplo.com"
										className="h-9 border-zinc-200 text-xs focus:border-zinc-400"
									/>
								</InputWrapperAuth>
							</div>

							<div className="space-y-1">
								<label className="ml-1 text-[8px] font-black uppercase tracking-widest text-zinc-500">
									Senha
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errorsRegister.password?.message}
									value={watchRegister('password')}
									displayError
								>
									<Input
										type="password"
										{...registerRegister('password')}
										data-testid={
											E2E_SCHEDULING.AUTH_REGISTER_PASSWORD
										}
										placeholder="Crie uma senha forte"
										className="h-9 border-zinc-200 text-xs focus:border-zinc-400"
									/>
								</InputWrapperAuth>
							</div>

							<div className="space-y-1">
								<label className="ml-1 text-[8px] font-black uppercase tracking-widest text-zinc-500">
									Repita a senha
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={
										errorsRegister.repeat_password?.message
									}
									value={watchRegister('repeat_password')}
									displayError
								>
									<Input
										type="password"
										{...registerRegister('repeat_password')}
										data-testid={
											E2E_SCHEDULING.AUTH_REGISTER_PASSWORD_REPEAT
										}
										placeholder="Confirme sua senha"
										className="h-9 border-zinc-200 text-xs focus:border-zinc-400"
									/>
								</InputWrapperAuth>
							</div>
						</>
					)}
				</div>

				<Button
					type="submit"
					data-testid={E2E_SCHEDULING.AUTH_SUBMIT}
					className="mt-2 w-full justify-center rounded-lg border-none bg-[#1a1a1a] py-3 text-sm font-bold text-white transition-all hover:bg-zinc-800"
					text={isLogin ? 'Entrar' : 'Criar conta'}
					endIcon={<ArrowRightCircle className="ml-1 h-4 w-4" />}
				/>
			</form>
		</section>
	);
};

export { PublicSessionAuth };
